import React, { useState, useEffect, useCallback } from 'react';
import { Search, Users, UserPlus, Edit, UserX, UserCheck, Download, ShoppingBag } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const AdminUsers = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', first_name: '', last_name: '', role: 'customer' });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: pagination.page, limit: 20 });
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);
      const res = await fetchWithAuth(`/api/admin/users?${params}`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) { 
      // Error handled silently
    }
    finally { setLoading(false); }
  }, [fetchWithAuth, pagination.page, search, roleFilter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const toggleUserStatus = async (userId) => {
    try {
      const res = await fetchWithAuth(`/api/admin/users/${userId}/toggle-status`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: data.is_active } : u));
        toast.success(data.is_active ? 'Usuario activado' : 'Usuario desactivado');
      }
    } catch (error) { toast.error('Error'); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const res = await fetchWithAuth('/api/admin/users', { method: 'POST', body: newUser });
      if (res.ok) {
        toast.success('Usuario creado');
        setShowCreateModal(false);
        setNewUser({ email: '', password: '', first_name: '', last_name: '', role: 'customer' });
        loadUsers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error');
      }
    } catch (error) { toast.error('Error de conexión'); }
  };

  const exportUsers = async () => {
    try {
      const res = await fetchWithAuth('/api/admin/export/users');
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        a.click();
        toast.success('Exportación completada');
      }
    } catch (error) { toast.error('Error'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(v);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX') : '-';

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-white">Usuarios</h2>
          <p className="text-gray-400 text-sm">{pagination.total} usuarios registrados</p>
        </div>
        <div className="flex gap-3">
          <button onClick={exportUsers} className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"><Download size={18} />Exportar</button>
          <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg"><UserPlus size={18} />Nuevo Usuario</button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadUsers()} placeholder="Buscar por email o nombre..." className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:border-amber-500" />
          </div>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white">
            <option value="">Todos los roles</option>
            <option value="customer">Clientes</option>
            <option value="admin">Administradores</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Usuario</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Email</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Rol</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Pedidos</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Total Gastado</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Estado</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-300">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-12 text-center text-gray-400"><Users className="mx-auto mb-3" size={40} /><p>No hay usuarios</p></td></tr>
              ) : users.map(user => (
                <tr key={user.id} className="hover:bg-gray-700/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center text-amber-500 font-medium">{user.first_name?.[0] || '?'}</div>
                      <div>
                        <p className="text-sm text-white font-medium">{user.first_name} {user.last_name}</p>
                        <p className="text-xs text-gray-400">Desde {formatDate(user.created_at)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="text-sm text-gray-300">{user.email}</span></td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${user.role === 'admin' || user.role === 'superadmin' ? 'bg-purple-500/10 text-purple-400' : 'bg-gray-600 text-gray-300'}`}>
                      {user.role === 'superadmin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Cliente'}
                    </span>
                  </td>
                  <td className="px-4 py-3"><span className="text-sm text-white">{user.order_count || 0}</span></td>
                  <td className="px-4 py-3"><span className="text-sm text-amber-500">{formatCurrency(user.total_spent || 0)}</span></td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${user.is_active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => toggleUserStatus(user.id)} className="p-2 text-gray-400 hover:text-amber-500" title={user.is_active ? 'Desactivar' : 'Activar'}>
                      {user.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <p className="text-sm text-gray-400">Página {pagination.page} de {pagination.pages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1} className="px-3 py-1.5 bg-gray-700 text-white rounded-lg disabled:opacity-50">Anterior</button>
              <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.pages} className="px-3 py-1.5 bg-gray-700 text-white rounded-lg disabled:opacity-50">Siguiente</button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowCreateModal(false)}>
          <div className="bg-gray-800 rounded-xl max-w-md w-full my-8 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <form onSubmit={createUser} className="p-6">
              <h3 className="text-xl font-semibold text-white mb-6">Nuevo Usuario</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Nombre</label>
                    <input type="text" value={newUser.first_name} onChange={e => setNewUser(prev => ({ ...prev, first_name: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white" required />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Apellido</label>
                    <input type="text" value={newUser.last_name} onChange={e => setNewUser(prev => ({ ...prev, last_name: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Email</label>
                  <input type="email" value={newUser.email} onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white" required />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Contraseña</label>
                  <input type="password" value={newUser.password} onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white" required />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Rol</label>
                  <select value={newUser.role} onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white">
                    <option value="customer">Cliente</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 bg-gray-700 text-white rounded-lg">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-amber-500 text-black font-medium rounded-lg">Crear Usuario</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
